import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";

import { apiRequest } from "../../../common/api/apiClient.js";
import type {
  ChannelDTO,
  MeetingTicketDTO,
  MessageDTO,
  MessagePageDTO,
} from "../../../common/api/types.js";

export const channelsQueryKey = ["chat", "channels"] as const;
export const messagesQueryKey = (channelId: string, focus = "") => ["chat", "messages", channelId, focus] as const;

export function useChannels(): UseQueryResult<ChannelDTO[], Error> {
  return useQuery({
    queryKey: channelsQueryKey,
    queryFn: () => apiRequest<ChannelDTO[]>("/chat/channels"),
  });
}

export function useMessages(channelId: string | null, focus: string | null = null): UseQueryResult<MessagePageDTO, Error> {
  return useQuery({
    queryKey: messagesQueryKey(channelId ?? "", focus ?? ""),
    queryFn: () => apiRequest<MessagePageDTO>(`/chat/channels/${channelId as string}/messages${focus ? `?focus=${encodeURIComponent(focus)}` : ""}`),
    enabled: channelId !== null,
  });
}

export function useOpenDirect() {
  const client = useQueryClient();
  return useMutation<ChannelDTO, Error, string>({
    mutationFn: (userId) =>
      apiRequest<ChannelDTO>("/chat/channels/direct", { method: "POST", body: { userId } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: channelsQueryKey });
    },
  });
}

export function useCreateGroup() {
  const client = useQueryClient();
  return useMutation<ChannelDTO, Error, { name: string; memberIds: string[] }>({
    mutationFn: (body) => apiRequest<ChannelDTO>("/chat/channels/group", { method: "POST", body }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: channelsQueryKey });
    },
  });
}

export function useSendMessage(channelId: string | null) {
  const client = useQueryClient();
  return useMutation<MessageDTO, Error, string>({
    mutationFn: (body) =>
      apiRequest<MessageDTO>(`/chat/channels/${channelId as string}/messages`, {
        method: "POST",
        body: { body },
      }),
    onSuccess: (message) => {
      // Die eigene Nachricht sofort anzeigen; über den Socket kommt sie ohnehin gleich.
      appendMessage(client, message);
      void client.invalidateQueries({ queryKey: channelsQueryKey });
    },
  });
}

export function useMarkRead() {
  const client = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (channelId) =>
      apiRequest<void>(`/chat/channels/${channelId}/read`, { method: "POST", body: {} }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: channelsQueryKey });
    },
  });
}

export function useSetChannelFavorite() {
  const client = useQueryClient();
  return useMutation<void, Error, { channelId: string; favorite: boolean }>({
    mutationFn: ({ channelId, favorite }) =>
      apiRequest<void>(`/chat/channels/${channelId}/favorite`, {
        method: "POST",
        body: { favorite },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: channelsQueryKey });
    },
  });
}

export function useJoinMeeting() {
  return useMutation<MeetingTicketDTO, Error, { channelId: string; audioOnly: boolean }>({
    mutationFn: ({ channelId, audioOnly }) =>
      apiRequest<MeetingTicketDTO>(`/chat/channels/${channelId}/meeting`, {
        method: "POST",
        body: { audioOnly },
      }),
  });
}

/** Hängt eine Nachricht an die geladene Seite an, ohne sie neu zu holen. */
export function appendMessage(
  client: ReturnType<typeof useQueryClient>,
  message: MessageDTO,
): void {
  client.setQueriesData<MessagePageDTO>({ queryKey: ["chat", "messages", message.channelId] }, (current) => {
    if (!current) {
      return current;
    }
    if (current.messages.some((entry) => entry.id === message.id)) {
      return current;
    }
    return { ...current, messages: [...current.messages, message] };
  });
}
